import type { TElementSpecifier } from "@de-xima/xima-common-js-dom";
import $ from "jquery";

import { createSimpleElFinder } from "../elfinder/ElFinder.ts";
import { createDeleteThemeCommand } from "./commands/delete-theme.js";
import { createCreateNewThemeCommand } from "./commands/create-new-theme.js";
import { createEditThemeCommand } from "./commands/edit-theme.js";
import {
  InfoItemThemeDescription,
  InfoItemThemeName,
  InfoItemParentTheme,
  InfoItemThemeUuid,
  InfoItemRelativeThemeFilePath,
} from "./info-item/form-theme-info-item.js";
import { createShowFcHelpCommand, showAdvancedEditModeHelpDialog } from "./commands/show-fc-help.js";
import { ElFinderFontQuickLookPlugin } from "./quick-look/font-quick-look-plugin.js";
import { parseString } from "@de-xima/xima-common-js-lang";
import { isFormThemeRootVolumeEntry } from "./client-form-theme-elfinder-helper.js";

declare global {
  namespace elFinder {
    export interface ElFinderCommandMap {
      createNewTheme: ElFinderCommandHandlerBaseStatic<BaseElFinderCommandOptions>;
      deleteTheme: ElFinderCommandHandlerBaseStatic<BaseElFinderCommandOptions>;
      editTheme: ElFinderCommandHandlerBaseStatic<BaseElFinderCommandOptions>;
      showFcHelp: ElFinderCommandHandlerBaseStatic<BaseElFinderCommandOptions>;
    }
  }
}

const Toolbar: string[][] = [
  ["createNewTheme", "editTheme", "deleteTheme"],
  ["mkdir", "mkfile", "upload", "download"],
  ["copy", "cut", "rm"],
  ["view", "sort", "fullscreen"],
  ["showFcHelp"],
  ["search"],
];

const ContextMenu: elFinder.ElFinderContextMenuSettings = {
  cwd: [
    "reload",
    "download",
    "|",
    "upload",
    "mkdir",
    "mkfile",
    "paste",
    "|",
    "view",
    "sort",
    "colwidth",
    "|",
    "info",
    "|",
    "fullscreen",
  ],
  files: [
    "editTheme",
    "deleteTheme",
    "|",
    "getfile",
    "|",
    "open",
    "download",
    "opendir",
    "quicklook",
    "|",
    "upload",
    "mkdir",
    "|",
    "copy",
    "cut",
    "paste",
    "duplicate",
    "extract",
    "|",
    "rm",
    "|",
    "rename",
    "edit",
    "resize",
    "|",
    "info",
  ],
  navbar: [
    "editTheme",
    "deleteTheme",
    "|",
    "open",
    "download",
    "|",
    "upload",
    "mkdir",
    "|",
    "copy",
    "cut",
    "paste",
    "duplicate",
    "|",
    "rm",
    "rename",
    "|",
    "info",
  ],
};

export function showAdvancedEditModeHelpDialogIfAvailable(): void {
  const instance = $("#resourceFileManager").elfinder("instance");
  if (instance !== undefined) {
    showAdvancedEditModeHelpDialog(instance);
  }
}

/**
 * Creates the ElFinder file explorer for editing client form themes.
 * @param element The element to create the ElFinder in.
 * @param settings Additional settings for the client form themes.
 */
export function createClientFormThemeElFinder(element: TElementSpecifier<HTMLElement>): void {
  elFinder.prototype.commands.createNewTheme = createCreateNewThemeCommand();
  elFinder.prototype.commands.deleteTheme = createDeleteThemeCommand();
  elFinder.prototype.commands.editTheme = createEditThemeCommand();
  elFinder.prototype.commands.showFcHelp = createShowFcHelpCommand();

  elFinder.prototype.commands.quicklook.plugins.unshift(ElFinderFontQuickLookPlugin);

  createSimpleElFinder(element, {
    customInfoItems: {
      relativeThemeFilePath: InfoItemRelativeThemeFilePath,
      themeUuid: InfoItemThemeUuid,
      themeName: InfoItemThemeName,
      parentTheme: InfoItemParentTheme,
      themeDescription: InfoItemThemeDescription,
    },
    customHandlers: {
      // When the user does a double click on a client form theme,
      // don't open the folder, but instead open the info dialog,
      // which lets the user edit the theme details (name, description, parent).
      dblclick: (e, fm) => {
        const hash = parseString(e.data.file);
        const file = fm.file(hash);
        if (isFormThemeRootVolumeEntry(file)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          fm.exec("info", hash);
        }
      },
    },

    hideInfoItems: ["locked", "group", "chmod", "aleasfor", "mode", "perms"],

    contextMenu: ContextMenu,

    // Allow images, videos, audios, fonts, plain text ,ttf
    displayInlineRegExp:
      /^(?:(?:image)|font\/(?:ttf|otf|woff|woff2)|application\/(?:x-mpegURL|dash+xml|font-woff|font-woff2|x-font-ttf|x-font-opentype)|(?:text\/plain|application\/pdf)$)/,

    toolbar: Toolbar,
  });
}
